<script>
    import axios from "axios";
    import { timeSince } from "@/state/helpers";

    export default {
        name: "CommentsSection",

        props: {
            cardData: {
                type: Object,
                default: () => ({})
            },
            cardType: {
                type: String,
                default: ''
            }
        },
        
        data () {
            return {
                localCardData: {},
                commentText: '',
                editCommentText: '',
                editingComment: false,
            }
        },

        watch: {
            cardData: {
                handler () {
                    this.localCardData = this.cardData;
                },
                deep: true
            }
        },
        
        computed: {
            userType () {
                return this.$store.getters['user/userType'];
            }
        },
        
        created () {
            this.localCardData = this.cardData;
        },
        
        methods: {
            async createComment() {
                this.$store.commit('loader/START_LOADING');
                try {
                    await axios.post(`/${this.cardType}/${this.localCardData._id}/comment`, {
                        text: this.commentText
                    });
                    this.commentText = '';
                    this.$emit('updated');
                } finally {
                    this.$store.commit('loader/END_LOADING');
                }
            },
            async updateComment(comment) {
                this.$store.commit('loader/START_LOADING');
                try {
                    await axios.put(`/${this.cardType}/${this.localCardData._id}/comment/${comment._id}`, {
                        text: this.editCommentText
                    });
                    this.editCommentText = '';
                    this.editingComment = false;
                    this.$emit('updated');
                } finally {
                    this.$store.commit('loader/END_LOADING');
                }
            },
            editComment(comment) {
                if (!this.editingComment) {
                    this.$set(comment, 'editMode', true);
                    this.editCommentText = comment.text;
                    this.editingComment = true;
                }
            },
            async deleteComment(comment) {
                this.$store.commit('loader/START_LOADING');
                try {
                    await axios.delete(`/${this.cardType}/${this.localCardData._id}/comment/${comment._id}`);
                    this.$emit('updated');
                } finally {
                    this.$store.commit('loader/END_LOADING');
                }
            },
            cancelCommentEditing(comment) {
                this.$delete(comment, 'editMode');
                this.editCommentText = '';
                this.editingComment = false;
            },
            timeAgo(time) {
                return `${timeSince(new Date(time))} ago`;
            }
        }
    }
</script>

<template>
    <div class="modal-section">
        <h5 class="mb-3 pb-1">Comments</h5>

        <div class="row">
            <div v-if="userType === 'client'" class="col-auto d-flex">
                <img v-if="localCardData.client_image" class="media-object rounded-circle avatar-xs" alt
                     :src="localCardData.client_image"/>
                <div v-else class="media-object avatar-xs letter-in-circle">
                    {{ localCardData.client_image_alt.charAt(0) }}
                </div>
            </div>
            <div v-if="userType === 'influencer'" class="col-auto d-flex">
                <img v-if="localCardData.influencer_image" class="media-object rounded-circle avatar-xs" alt
                     :src="localCardData.influencer_image"/>
                <div v-else class="media-object avatar-xs letter-in-circle">
                    {{ localCardData.influencer_image_alt.charAt(0) }}
                </div>
            </div>
            <div class="col pl-0">
                <textarea-autosize
                        class="form-control"
                        placeholder="Write a comment..."
                        v-model="commentText"
                        :min-height="60"
                        :max-height="350"/>
                <button @click="createComment" v-if="commentText.length" class="btn btn-sm btn-primary mt-2"> Send </button>
            </div>
        </div>

        <div v-for="comment in localCardData.comments" :key="comment._id" class="media mt-3">
            <div class="mr-3">
                <div v-if="comment.commenter_type === 'client'">
                    <img v-if="localCardData.client_image" class="media-object rounded-circle avatar-xs"
                         :src="localCardData.client_image"/>
                    <div v-else class="media-object avatar-xs letter-in-circle">
                        {{ localCardData.client_image_alt.charAt(0) }}
                    </div>
                </div>
                <div v-if="comment.commenter_type === 'influencer'">
                    <img v-if="localCardData.influencer_image" class="media-object rounded-circle avatar-xs"
                         :src="localCardData.influencer_image"/>
                    <div v-else class="media-object avatar-xs letter-in-circle">
                        {{ localCardData.influencer_image_alt.charAt(0) }}
                    </div>
                </div>
            </div>
            <div class="media-body">
                <h5 class="font-size-13 mb-1">
                    {{ comment.commenter_type === 'client' ? localCardData.client_image_alt : localCardData.influencer_image_alt }}
                    <span class="text-muted font-weight-normal ml-2 font-size-12">{{ timeAgo(comment.created_at) }}</span>
                    <span v-if="comment.edited" class="text-muted font-weight-normal ml-2 font-size-12">(edited)</span>
                </h5>
                <div v-if="comment.editMode">
                    <textarea-autosize
                            class="form-control"
                            placeholder="Write a comment..."
                            v-model="editCommentText"
                            :min-height="60"
                            :max-height="350"/>
                    <button @click="updateComment(comment)" class="btn btn-sm btn-primary mt-2">Update</button>
                    <button @click="cancelCommentEditing(comment)" class="btn btn-sm btn-light mt-2 ml-2"><i
                            class="bx bx-x font-size-18 align-middle"></i></button>
                </div>
                <p v-else class="text-muted mb-1 comment-content">
                    {{ comment.text }}
                </p>
                <div v-if="comment.commenter_type === userType && !comment.editMode">
                    <span @click="editComment(comment)" class="text-muted comment-link font-size-12">Edit</span>
                    <span @click="deleteComment(comment)" class="text-muted ml-2 comment-link font-size-12">Delete</span>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .comment-content {
        white-space: pre-wrap;
        background: #fbfbfb;
        border-radius: 4px;
        padding: 10px;
        box-shadow: 0px 3px 4px 1px #efefef;
    }
    .comment-link {
        cursor: pointer;
        text-decoration: underline;
    }
</style>